/* «Вы» и «Близкие» с сервера для общего модуля BotPeople: экраны и «Профиль» читают одни и те же живые списки. */
import { endpoints } from '../../api';
import { ApiError, uuid } from '../../api/client';
import type { Person, PersonChanges, SelfProfile, UserAvatar } from '../../api/people';
import { UploadRejectedError } from '../../api/uploads';
import { safeMediaUrl } from '../studio/studio-content';
import { accountLocale, uiDate, uiFormat, uiText } from '../../i18n';

interface LegacyPhoto {
  url: string;
  id: string;
}

/** Снимок черновика редактора: с сервера (id) или только что выбранный файл (file) */
export interface DraftPhoto {
  url: string;
  id?: string;
  file?: Blob;
  cam?: boolean;
  /** Ключ загрузки: повтор после сбоя не создаёт второй slot */
  key?: string;
}

export interface PersonDraft {
  n: string;
  rel: string;
  age: string | null;
  photos: DraftPhoto[];
}

interface LegacyPerson {
  id: number;
  n: string;
  rel: string;
  age: string | null;
  photos: LegacyPhoto[];
  version: number;
  selectable: boolean;
  /** Почему выбрать нельзя (сервер), когда selectable false: BotPeople его не читает — отказ даёт engine-kit/people-bridge.ts */
  disabled_reason?: string | null;
}

interface LegacyVariant {
  id: number;
  vn: string;
  name: string;
  full: string;
  base: string;
  url: string | null;
  /** Ключ картинки для ASSET() (window.__ASSET_URL[img] — адрес превью): каталог персонажей «Мои» и экраны шаблона читают вариант по img */
  img?: string;
  y: number;
  /** Цвета силуэта без картинки */
  c1?: string;
  c2?: string;
  voices: number[];
  c: string[];
  src: string;
  status: string;
  version: number;
}

interface PeopleModule {
  store: { all(): LegacyPerson[] };
  me: {
    variants(): LegacyVariant[];
    set(o: { photos?: LegacyPhoto[]; pick?: string | number; why?: string }): unknown;
  };
  connect(server: typeof peopleServer | null): void;
}

const PET = 'Питомец';
const AGES = new Set(['kid', 'adult', 'old']);
/** Страниц вариантов за один вход: больше пользователь не создаёт, а бесконечный цикл недопустим */
const AVATAR_PAGES = 10;

const photo = (value: { url: string; upload_id: string }): LegacyPhoto => ({ url: value.url, id: value.upload_id });

function person(value: Person): LegacyPerson {
  return {
    id: value.id,
    n: value.name,
    rel: value.is_pet ? PET : value.rel,
    age: value.is_pet ? null : value.age && AGES.has(value.age) ? value.age : 'adult',
    photos: value.photos.map(photo),
    version: value.version,
    selectable: value.selectable,
    disabled_reason: value.disabled_reason ?? null,
  };
}

const STYLES = new Set(['real', 'illus', '3d']);
const assetUrls = () => {
  const w = window as unknown as { __ASSET_URL?: Record<string, string> | null };
  return (w.__ASSET_URL ||= {});
};
/** «Создан 1 октября» — подпись шаблона (addAvatar: 'Создан '+BotCore.date(at, true)); без даты или без BotCore — пусто */
function createdLine(at: string | null | undefined): string {
  const t = at ? Date.parse(at) : NaN;
  const date = (window.BotCore as unknown as { date?: (t: number, long?: boolean) => string } | undefined)?.date;
  return Number.isFinite(t) && typeof date === 'function' ? uiFormat('Создан {0}', accountLocale() !== 'ru' ? uiDate(t, { day: 'numeric', month: 'long' }) : date(t, true)) : '';
}

function variant(value: UserAvatar): LegacyVariant {
  const name = 'Вы · ' + value.variant_name;
  /* у шаблона превью варианта — файл из assets/ в поле img (varUrl, каталог персонажей «Мои» app.html cfaceHTML: ASSET(a.img));
     адрес сервера встаёт под своим ключом в __ASSET_URL, как портреты персонажей (bridge/server-characters.ts) */
  const img = `srv-var-${value.id}`;
  const face = safeMediaUrl(value.image_url);
  if (face) assetUrls()[img] = face;
  else delete assetUrls()[img];
  return {
    id: value.id,
    vn: value.variant_name,
    name,
    full: name,
    base: value.base && STYLES.has(value.base) ? value.base : 'real',
    url: value.image_url,
    ...(face ? { img } : {}),
    y: 20,
    ...(value.tint ? { c1: value.tint.from, c2: value.tint.to } : {}),
    voices: value.voice_id === null ? [] : [value.voice_id],
    c: [],
    src: value.status === 'ready' ? createdLine(value.created_at) : uiText('Создаётся'),
    status: value.status,
    version: value.version,
  };
}

async function avatars(): Promise<UserAvatar[]> {
  const items: UserAvatar[] = [];
  let before: number | undefined;
  for (let page = 0; page < AVATAR_PAGES; page++) {
    const result = await endpoints.people.avatars(before);
    items.push(...result.items);
    if (result.next_before_id === null) break;
    before = result.next_before_id;
  }
  return items;
}

const module = () => window.BotPeople as PeopleModule | undefined;

/** Версия «Вы» для If-Match: у пустого профиля 0, запись без неё сервер не примет */
let selfVersion = 0;

function applySelf(people: PeopleModule, self: SelfProfile | null) {
  selfVersion = self?.version ?? 0;
  people.me.set({ photos: self ? self.photos.map(photo) : [], pick: self?.pick ?? 'photos', why: 'server' });
}

class PeopleError extends Error {}

const asFile = (blob: Blob) => (blob instanceof File ? blob : new File([blob], 'photo.jpg', { type: blob.type || 'image/jpeg' }));

/** Новые снимки уходят в хранилище до записи человека; уже загруженные идут своим id */
function uploadPhotos(photos: DraftPhoto[]): Promise<string[]> {
  return Promise.all(photos.map(async (item) => {
    if (item.id) return item.id;
    if (!item.file) throw new PeopleError('photo_missing');
    item.key ??= uuid();
    const done = await endpoints.uploadFile(asFile(item.file), { purpose: 'avatar_photo', kind: 'photo', origin: item.cam ? 'camera' : 'file' }, item.key);
    item.id = done.id;
    return done.id;
  }));
}

/** Подписанный адрес сервера живёт минуты; свой только что выбранный снимок показываем из памяти */
function keepLocal(saved: LegacyPhoto[], draft: DraftPhoto[]): LegacyPhoto[] {
  return saved.map((item) => {
    const local = draft.find((d) => d.id === item.id && d.url.startsWith('blob:'));
    return local ? { url: local.url, id: item.id } : item;
  });
}

const changes = (draft: PersonDraft, ids: string[]): PersonChanges =>
  ({ name: draft.n, rel: draft.rel, age: draft.age, photo_upload_ids: ids });

/** Запись аккаунта для BotPeople.connect: редакторы людей сохраняют через неё, память меняется только после ответа */
export const peopleServer = {
  async person(current: LegacyPerson | null, draft: PersonDraft, memo: { key?: string }): Promise<LegacyPerson> {
    const body = changes(draft, await uploadPhotos(draft.photos));
    const saved = current
      ? await endpoints.people.update(current.id, body, current.version)
      : await endpoints.people.create(body, (memo.key ??= uuid()));
    const result = person(saved);
    result.photos = keepLocal(result.photos, draft.photos);
    return result;
  },
  drop: (id: number) => endpoints.people.remove(id),
  async self(o: { photos?: DraftPhoto[]; pick?: string | number }): Promise<{ photos: LegacyPhoto[]; pick: string | number }> {
    const body: { photo_upload_ids?: string[]; pick?: 'photos' | number } = {};
    if (o.photos) body.photo_upload_ids = await uploadPhotos(o.photos);
    if (o.pick != null) body.pick = o.pick === 'photos' ? 'photos' : Number(o.pick);
    const saved = await endpoints.people.updateSelf(body, selfVersion);
    selfVersion = saved.version;
    return { photos: keepLocal(saved.photos.map(photo), o.photos ?? []), pick: saved.pick };
  },
  async renameVariant(current: LegacyVariant, name: string): Promise<LegacyVariant> {
    return variant(await endpoints.people.updateAvatar(current.id, { variant_name: name }, current.version));
  },
  dropVariant: (id: number) => endpoints.people.removeAvatar(id),
  error(e: unknown): string {
    if (e instanceof UploadRejectedError) return uiText('Фото не подошло. Выберите другое');
    if (e instanceof PeopleError) return uiText('Загрузите фото заново');
    if (e instanceof ApiError && e.status === 412) {
      void syncPeople().catch(() => {});
      return uiText('Данные изменились. Откройте заново');
    }
    return uiText('Не удалось сохранить. Попробуйте ещё раз');
  },
};

/** Редакторы людей пишут в аккаунт, а не в память прототипа */
export function connectPeople() {
  module()?.connect(peopleServer);
  if (!watchingExpired) {
    watchingExpired = true;
    /* Адреса фото «Вы» и близких — подписанные и временные (GET /app/people/me): экран, открытый дольше их срока, показывал битую
       картинку («После» у «Повтори движения», владелец 02.11.2026). Картинка с таким адресом не загрузилась — люди перечитываются
       (не чаще раза в 30 с), экраны перерисовываются по bot:avatars */
    document.addEventListener(
      'error',
      e => {
        const img = e.target;
        if (!(img instanceof HTMLImageElement) || !/\/miniapp\/verified\/.*X-Amz-Expires=/.test(img.src)) return;
        const now = Date.now();
        if (now - resyncedAt < 30_000) return;
        resyncedAt = now;
        void syncPeople();
      },
      true,
    );
  }
}
let watchingExpired = false;
let resyncedAt = 0;

/** Демо-люди и демо-вариант прототипа не должны мелькнуть до ответа сервера */
export function clearPeople() {
  const people = module();
  if (!people) return;
  people.store.all().splice(0);
  people.me.variants().splice(0);
  applySelf(people, null);
}

/** Перечитать «Вы», близких и варианты. Каждая часть независима: недоступная не стирает остальные */
export async function syncPeople(): Promise<void> {
  const people = module();
  if (!people) return;
  const [self, list, own] = await Promise.allSettled([endpoints.people.self(), endpoints.people.list(), avatars()]);
  if (list.status === 'fulfilled') people.store.all().splice(0, Infinity, ...list.value.items.map(person));
  if (own.status === 'fulfilled') people.me.variants().splice(0, Infinity, ...own.value.map(variant));
  if (self.status === 'fulfilled') applySelf(people, self.value);
  document.dispatchEvent(new CustomEvent('bot:avatars', { detail: { av: null, list: people.me.variants() } }));
}
